import { PlaceDetails } from '@/map/domain/entities/map.types';
import { useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { GoogleMapsService } from '@/map/application/services/googleMapsService';
import { LocationFormData } from './validationSchema';

const COORD_KEY_PRECISION = 5;
const ZIP_CODE_MAX_LENGTH = 10;

function coordKey(lat: number, lng: number): string {
  return `${Number(lat.toFixed(COORD_KEY_PRECISION))}-${Number(lng.toFixed(COORD_KEY_PRECISION))}`;
}

interface ZipPrefillFromGeocodeProps {
  setSelectedPlaceDetails: React.Dispatch<React.SetStateAction<PlaceDetails | null>>;
}

/**
 * Runs inside APIProvider so useMapsLibrary('geocoding') is available.
 * Reverse-geocodes lat/lng to pre-fill the zipCode field when the backend doesn't provide it (e.g. empty string).
 */
export function ZipPrefillFromGeocode({ setSelectedPlaceDetails }: ZipPrefillFromGeocodeProps) {
  const formik = useFormikContext<LocationFormData>();
  const geocodingLib = useMapsLibrary('geocoding');
  const zipFetchedForCoordRef = useRef<string | null>(null);

  useEffect(() => {
    if (!geocodingLib) return;

    const { latitude, longitude, zipCode } = formik.values;
    const hasCoordinates = latitude != null && longitude != null;
    const missingZip = !zipCode || String(zipCode).trim() === '';
    const key = hasCoordinates ? coordKey(latitude, longitude) : null;
    const alreadyFetched = key !== null && zipFetchedForCoordRef.current === key;

    if (!hasCoordinates || !missingZip || alreadyFetched) return;

    zipFetchedForCoordRef.current = key;

    const Geocoder = geocodingLib.Geocoder;
    const geocoder = new Geocoder();
    const googleMapsService = new GoogleMapsService();

    geocoder
      .geocode({ location: { lat: latitude, lng: longitude } })
      .then((result) => {
        if (!result.results[0]?.address_components?.length) return;
        const details = googleMapsService.extractAddressComponents(result.results[0].address_components);
        const fetchedZip = details.zipCode?.trim();
        if (fetchedZip) {
          const truncated = fetchedZip.substring(0, ZIP_CODE_MAX_LENGTH);
          formik.setFieldValue('zipCode', truncated);
          setSelectedPlaceDetails((prev) => (prev ? { ...prev, zipCode: truncated } : null));
        }
      })
      .catch(() => {
        zipFetchedForCoordRef.current = null;
      });
  }, [geocodingLib, formik.values.latitude, formik.values.longitude, formik.values.zipCode, formik.setFieldValue, setSelectedPlaceDetails]);

  return null;
}
